import type { StudyInputSpec } from '@/features/chart/lib/config/chart-analytics'

// TV metainfo input element (_metainfoVersion 53). Built structurally: the
// charting_library enums live in ambient .d.ts files with no runtime module,
// so string literals are the only option. A dropdown is 'text' + options
// (StudyTextInputInfo) — v53 has no 'select' type.
export type TVStudyInputInfo = {
  id: string
  name: string
  type: 'text' | 'bool' | 'integer' | 'float'
  defval: string | number | boolean
  options?: string[]
  optionsTitles?: Record<string, string>
  isHidden?: boolean
}

export type TVStudyInputDefaults = Record<string, string | number | boolean>

/** Map a definition's input specs to TV metainfo.inputs[] + defaults.inputs{}. */
export function buildStudyInputs(specs: StudyInputSpec[] | undefined): {
  inputs: TVStudyInputInfo[]
  defaults: TVStudyInputDefaults
} {
  const resolved = specs ?? []
  const inputs = resolved.map((spec) => ({
    id: spec.id,
    name: spec.name,
    type: spec.type === 'select' ? ('text' as const) : spec.type,
    defval: spec.defval,
    ...(spec.isHidden !== undefined ? { isHidden: spec.isHidden } : {}),
    ...(spec.type === 'select'
      ? {
          options: spec.options ?? [],
          ...(spec.optionsTitles ? { optionsTitles: spec.optionsTitles } : {}),
        }
      : {}),
  }))
  const defaults = Object.fromEntries(resolved.map((spec) => [spec.id, spec.defval]))
  return { inputs, defaults }
}

/** Read current input values inside constructor.main. TV passes an index-based
 *  callback ordered like metainfo.inputs; fall back to defval when the value is
 *  absent (TV probes main once with empty context to discover variables). */
export function readStudyInputValues(
  specs: StudyInputSpec[] | undefined,
  inputCallback: ((index: number) => string | number | boolean | undefined) | undefined
): Record<string, string | number | boolean> {
  const values: Record<string, string | number | boolean> = {}
  ;(specs ?? []).forEach((spec, index) => {
    values[spec.id] = inputCallback?.(index) ?? spec.defval
  })
  return values
}

/** Next value for the hidden refreshNonce input. Strictly increasing, and
 *  Date.now()-floored so it always differs from any nonce persisted in a
 *  restored layout — TV only recomputes on an actual value CHANGE. */
export function nextRefreshNonce(previous: number): number {
  return Math.max(previous + 1, Date.now())
}
